import { Injectable } from '@angular/core';
import { ServiceBase } from '../servicebase';
import { HttpService } from '../http.service';
import { map } from 'rxjs/operators';
import { TimeShiftModel } from 'services/models/hiring/timeShift.model';
import { Observable, of } from 'rxjs';
import { Response } from 'services/models/response';
import { SequenceTimeShiftModel } from 'services/models/hiring/sequenceTimeShift.model';
import { TimeShiftHistoryModel } from 'services/models/corporate/timeShiftHistory.model';
import { DynamicModel } from 'services/models/dynamic.model';
import { environment } from 'environments/environment';

@Injectable()
export class TimeShiftService extends ServiceBase<TimeShiftModel> {
  constructor(http: HttpService) {
    super('timeshift', http, environment.apiUrl);
  }

  createEntity(input: any): TimeShiftModel {
    return TimeShiftModel.create(input);
  }

  getSequencesByTimeShift(timeShiftId: string): Observable<Response<SequenceTimeShiftModel[]>> {
    return this.http.get(`${this.endpoint}/${timeShiftId}/sequences`).pipe(
      map(res => {
        const response = new Response<SequenceTimeShiftModel[]>();
        response.data = res.body.map(
          pp => SequenceTimeShiftModel.create(pp) as SequenceTimeShiftModel
        );
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  getTableSequenceOptions(date: string, timeShiftId: string): Observable<Response<DynamicModel>> {
    return this.http.get(`${this.endpoint}/${date}/${timeShiftId}/table-sequence-options`).pipe(
      map(res => {
        const response = new Response<DynamicModel>();
        response.data = DynamicModel.create(res.body) as DynamicModel;
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  /** False = empresa RM (UI só data + turno). True = Protheus (pede sequencial/tabela). */
  requiresSequence(companyId: string): Observable<boolean> {
    return this.http.get(`${this.endpoint}/requires-sequence/${companyId}`).pipe(
      map(res => {
        const body = res.body || {};
        if (typeof body.requiresSequence === 'boolean') {
          return body.requiresSequence;
        }
        if (typeof body.RequiresSequence === 'boolean') {
          return body.RequiresSequence;
        }
        return true;
      })
    );
  }

  listByEmployee(employeeId: string): Observable<Response<TimeShiftHistoryModel[]>> {
    return this.http.get(`${this.endpoint}/history-by-employee/${employeeId}`).pipe(
      map(res => {
        const response = new Response<TimeShiftHistoryModel[]>();
        response.data = res.body.map(pp => TimeShiftHistoryModel.create(pp));
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  getSuggestSequence(id: string, employeeId: string, dateProjection: string): Observable<string> {
    return new Observable<string>(observer => {
      this.http
        .get(`${this.endpoint}/${id}/suggest-sequence/${employeeId}/${dateProjection}`)
        .subscribe(
          res => {
            observer.next(res.body.sequence);
            observer.complete();
          },
          err => observer.error(err)
        );
    });
  }

  deleteTimeShiftHistory(timeShiftHistory: TimeShiftHistoryModel) {
    return this.http.post(`${this.endpoint}/delete-time-hift-history`, timeShiftHistory);
  }

  loadTimeShiftsByParams({
    companyId,
    workScaleGroup,
  }: {
    companyId: string;
    workScaleGroup?: string;
  }): Observable<TimeShiftModel[]> {
    const select = [
      'id',
      'title',
      'code',
      'companyId',
      'companyGroupId',
      'enable',
      'creationDate',
      'workScale',
      'workScaleGroup',
    ].join(',');

    let filter = `companyId eq ${companyId}`;
    if (workScaleGroup) {
      filter += ` and workScaleGroup eq '${workScaleGroup}'`;
    }

    const orderBy = `$orderby=code asc`;
    const queryUrl = [`$select=${select}`, orderBy, `$filter=${filter}`].join('&');

    return this.http.get(`${this.endpoint}/odata?${queryUrl}`, null, null, null, true).pipe(
      map(res => {
        return res.body.filter(x => x.enable).map(item => TimeShiftModel.create(item));
      })
    );
  }

  getListFiltered(params?: string[]): Observable<TimeShiftModel[]> {
    const queryParams = params?.length ? `?${params.join('&')}` : '';

    return this.http
      .get(`${this.endpoint}/odata${queryParams}`, null, null, null, true)
      .pipe(map((data: any) => data.body.map((item: any) => TimeShiftModel.create(item))));
  }

  listByHierarchyAndCompany(companyId: string): Observable<Response<TimeShiftModel[]>> {
    if (!companyId) companyId = '00000000-0000-0000-0000-000000000000';

    return this.http
      .get(
        `${this.endpoint}/list-by-hierarchy-and-company/${companyId}`,
        undefined,
        undefined,
        undefined,
        true
      )
      .pipe(
        map(res => {
          const response = new Response<TimeShiftModel[]>();
          response.data = res.body
            .filter((x: { enable: boolean }) => x.enable)
            .map((pp: unknown) => TimeShiftModel.create(pp));
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }

  listByCompany(companyId: string): Observable<Response<TimeShiftModel[]>> {
    if (!companyId) companyId = '00000000-0000-0000-0000-000000000000';

    return this.loadTimeShiftsByParams({ companyId }).pipe(
      map(data => {
        const response = new Response<TimeShiftModel[]>();
        response.data = data;
        response.count = data.length;
        return response;
      })
    );
  }

  listByCompanies(companyIds: string[]): Observable<TimeShiftModel[]> {
    if (!companyIds?.length) {
      return of([]);
    }

    const companyFilter = companyIds.map(id => `companyId eq ${id}`).join(' or ');

    const filter = `(${companyFilter}) and enable eq true`;

    const query =
      `${this.endpoint}/odata` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=id,title,code,companyName` +
      `&$orderby=title asc`;

    return this.http
      .get(query, null, null, null, true)
      .pipe(map((res: any) => res.body.map((item: any) => TimeShiftModel.create(item))));
  }
}
